import React, { useState, useEffect } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import { Users, Calendar, FileText, TrendingUp, Clock, AlertCircle, RefreshCw, Activity, Stethoscope, ChevronDown } from 'lucide-react';
import { Link } from 'react-router-dom';
import api from '../api';
import { RecertDueCard } from '../components/RecertDueCard';
import { BulkAutoFillCard } from '../components/BulkAutoFillCard';
import { NurseWorkload } from '../components/NurseWorkload';
import { TodayAgenda } from '../components/TodayAgenda';
import { MyCaseloadBoard } from '../components/MyCaseloadBoard';
import { TimeClockCard } from '../components/timeclock/TimeClockCard';
import { SignedAlertBanner } from '../components/SignedAlertBanner';

const DashboardHome = () => {
  const [stats, setStats] = useState({
    activePatients: 0,
    todayVisits: 0,
    pendingDocumentation: 0,
    upcomingAdmissions: 0,
    revenueThisMonth: '$0',
    averageRating: 0
  });
  const [patients, setPatients] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [alertCount, setAlertCount] = useState(0);
  const [recentSkilledNursingForms, setRecentSkilledNursingForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [patientDropdownOpen, setPatientDropdownOpen] = useState(false);
  const [clinicalDropdownOpen, setClinicalDropdownOpen] = useState(false);

  // Patient menu items (matching sidebar)
  const patientMenuItems = [
    { name: 'Patient Demographics', href: '/dashboard/patients/demographics' },
    { name: 'Admission, Insurance & Prior Auth', href: '/dashboard/patients/admission' },
    { name: 'Patient Chart', href: '/dashboard/patients/chart' },
    { name: 'Patient Communication Log', href: '/dashboard/patients/communication' },
  ];

  // Clinical menu items (matching sidebar)
  const clinicalMenuItems = [
    { name: 'OASIS-E2 Assessments (SOC/ROC/FU/DC)', href: '/dashboard/forms/oasis' },
    { name: 'Non-OASIS SOC Assessment', href: '/dashboard/forms/non-oasis' },
    { name: 'SOC/ROC Comprehensive Assessment', href: '/dashboard/forms/soc-roc' },
    { name: 'Discharge Comprehensive Assessment', href: '/dashboard/forms/discharge-v2' },
    { name: 'Recertification Assessment', href: '/dashboard/forms/recertification' },
    { name: '485 - Certification and Plan of Care', href: '/dashboard/clinical/poc-485' },
    { name: 'Plan of Care Plus', href: '/dashboard/clinical/poc-plus' },
    { name: 'Orders & Documents', href: '/dashboard/clinical/orders' },
    { name: 'Order Plus (Verbal Order Plus)', href: '/dashboard/clinical/order-plus' },
    { name: 'Medication Profile', href: '/dashboard/clinical/medication-profile' },
    { name: 'Medication Administration Log', href: '/dashboard/clinical/medication-log' },
    { name: 'Physical Therapy Documents', href: '/dashboard/clinical/pt-documents' },
    { name: 'Occupational Therapy Documents', href: '/dashboard/clinical/ot-documents' },
    { name: 'Speech Therapy Documents', href: '/dashboard/clinical/st-documents' },
    { name: 'Medical Social Worker Documents', href: '/dashboard/clinical/msw-documents' },
    { name: 'Aide Documents', href: '/dashboard/clinical/aide-documents' },
    { name: 'Non-Skilled Assessment', href: '/dashboard/clinical/non-skilled' },
    { name: 'Electronic Health Records', href: '/dashboard/clinical/ehr' },
    { name: 'Braden Scale', href: '/dashboard/clinical/braden-scale' },
    { name: 'Supervisory Visit Notes', href: '/dashboard/clinical/supervisory-notes' },
    { name: 'Incident Report', href: '/dashboard/clinical/incident-report' },
  ];

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      // Fetch all data in parallel
      const [statsRes, patientsRes, appointmentsRes, alertsRes, nursesNotesRes, woundCareRes, ivTherapyRes, pcaRes] = await Promise.all([
        api.get('/dashboard/stats'),
        api.get('/patients'),
        api.get('/appointments'),
        api.get('/alerts/count'),
        api.get('/skilled-nursing/nurses-notes').catch(() => ({ data: { forms: [] } })),
        api.get('/skilled-nursing/wound-care').catch(() => ({ data: { forms: [] } })),
        api.get('/skilled-nursing/iv-therapy').catch(() => ({ data: { forms: [] } })),
        api.get('/skilled-nursing/pca').catch(() => ({ data: { forms: [] } }))
      ]);

      if (statsRes.data.success) {
        setStats(statsRes.data.stats);
      }
      if (patientsRes.data.success) {
        setPatients(patientsRes.data.patients.slice(0, 3));
      }
      if (appointmentsRes.data.success) {
        // Filter for today's appointments
        const today = new Date().toISOString().split('T')[0];
        const todayAppointments = appointmentsRes.data.appointments.filter(
          a => a.date === today
        );
        setAppointments(todayAppointments.length > 0 ? todayAppointments : appointmentsRes.data.appointments.slice(0, 3));
      }
      if (alertsRes.data.success) {
        setAlertCount(alertsRes.data.urgent || 0);
      }
      
      // Combine and sort recent skilled nursing forms
      const allForms = [
        ...(nursesNotesRes.data.forms || []).map(f => ({ ...f, formTypeLabel: 'Nurses Notes', formTypeIcon: '📋', link: `/dashboard/skilled-nursing/nurses-notes?id=${f.id}` })),
        ...(woundCareRes.data.forms || []).map(f => ({ ...f, formTypeLabel: 'Wound Care', formTypeIcon: '🩹', link: `/dashboard/skilled-nursing/wound-care?id=${f.id}` })),
        ...(ivTherapyRes.data.forms || []).map(f => ({ ...f, formTypeLabel: 'IV Therapy', formTypeIcon: '💉', link: `/dashboard/skilled-nursing/iv-therapy?id=${f.id}` })),
        ...(pcaRes.data.forms || []).map(f => ({ ...f, formTypeLabel: 'PCA', formTypeIcon: '👩‍⚕️', link: `/dashboard/skilled-nursing/pca?id=${f.id}` }))
      ].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)).slice(0, 5);
      
      setRecentSkilledNursingForms(allForms);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 animate-spin text-[#ea580c]" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="dashboard-home">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-800">Dashboard</h1>
            <p className="text-gray-600 mt-1">Welcome back! Here's what's happening today.</p>
          </div>
          <button 
            onClick={fetchDashboardData}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            title="Refresh"
          >
            <RefreshCw className="w-5 h-5 text-gray-600" />
          </button>
        </div>

        <SignedAlertBanner />
        <BulkAutoFillCard />

        {/nurse|clinician|\brn\b|lvn|lpn|therapist/i.test(JSON.parse(localStorage.getItem('ivory_user') || '{}').role || '') && <TimeClockCard />}
        <MyCaseloadBoard />

        <TodayAgenda />

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <RecertDueCard />
          {!/nurse|clinician|\brn\b|lvn|lpn/i.test(JSON.parse(localStorage.getItem('ivory_user') || '{}').role || '') && <NurseWorkload compact />}
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Active Patients</p>
                <p className="text-3xl font-bold text-gray-800">{stats.activePatients}</p>
              </div>
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <Users className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Today's Visits</p>
                <p className="text-3xl font-bold text-gray-800">{stats.todayVisits}</p>
              </div>
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                <Calendar className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Pending Documentation</p>
                <p className="text-3xl font-bold text-gray-800">{stats.pendingDocumentation}</p>
              </div>
              <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                <FileText className="w-6 h-6 text-orange-600" />
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Upcoming Admissions</p>
                <p className="text-3xl font-bold text-gray-800">{stats.upcomingAdmissions}</p>
              </div>
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                <Clock className="w-6 h-6 text-purple-600" />
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200 relative">
            <button 
              onClick={() => setPatientDropdownOpen(!patientDropdownOpen)}
              className="w-full flex items-center justify-between"
              data-testid="patient-dropdown-btn"
            >
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                  <Users className="w-6 h-6 text-green-600" />
                </div>
                <span className="text-xl font-semibold text-gray-800">Patient</span>
              </div>
              <ChevronDown className={`w-5 h-5 text-gray-500 transition-transform ${patientDropdownOpen ? 'rotate-180' : ''}`} />
            </button>
            {patientDropdownOpen && (
              <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-64 overflow-y-auto">
                {patientMenuItems.map((item, index) => (
                  <Link
                    key={index}
                    to={item.href}
                    className="block px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 border-b border-gray-100 last:border-b-0"
                    onClick={() => setPatientDropdownOpen(false)}
                  >
                    {item.name}
                  </Link>
                ))}
              </div>
            )}
          </div>

          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200 relative">
            <button 
              onClick={() => setClinicalDropdownOpen(!clinicalDropdownOpen)}
              className="w-full flex items-center justify-between"
              data-testid="clinical-dropdown-btn"
            >
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center">
                  <Stethoscope className="w-6 h-6 text-yellow-600" />
                </div>
                <span className="text-xl font-semibold text-gray-800">Clinical</span>
              </div>
              <ChevronDown className={`w-5 h-5 text-gray-500 transition-transform ${clinicalDropdownOpen ? 'rotate-180' : ''}`} />
            </button>
            {clinicalDropdownOpen && (
              <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-64 overflow-y-auto">
                {clinicalMenuItems.map((item, index) => (
                  <Link
                    key={index}
                    to={item.href}
                    className="block px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 border-b border-gray-100 last:border-b-0"
                    onClick={() => setClinicalDropdownOpen(false)}
                  >
                    {item.name}
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Two Column Layout */}
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Recent Patients */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-xl font-bold text-gray-800">Recent Patients</h2>
              <Link to="/dashboard/patients" className="text-sm text-[#ea580c] hover:underline">View all</Link>
            </div>
            <div className="p-6">
              {patients.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Users className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                  <p>No patients yet</p>
                  <Link to="/dashboard/patients" className="text-[#ea580c] hover:underline text-sm">Add your first patient</Link>
                </div>
              ) : (
                <div className="space-y-4">
                  {patients.map((patient) => (
                    <div key={patient.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-[#ea580c] rounded-full flex items-center justify-center">
                          <span className="text-white font-semibold">
                            {patient.firstName[0]}{patient.lastName[0]}
                          </span>
                        </div>
                        <div>
                          <p className="font-semibold text-gray-800">{patient.firstName} {patient.lastName}</p>
                          <p className="text-sm text-gray-600">{patient.primaryDiagnosis}</p>
                        </div>
                      </div>
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                        patient.status === 'Active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                      }`}>
                        {patient.status}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Recent Skilled Nursing Forms */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <Activity className="w-5 h-5 text-[#f39c12]" />
                <h2 className="text-xl font-bold text-gray-800">Recent Skilled Nursing Forms</h2>
              </div>
            </div>
            <div className="p-6">
              {recentSkilledNursingForms.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <FileText className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                  <p>No skilled nursing forms yet</p>
                  <div className="flex justify-center gap-2 mt-3 flex-wrap">
                    <Link to="/dashboard/skilled-nursing/nurses-notes" className="text-[#ea580c] hover:underline text-sm">Nurses Notes</Link>
                    <span className="text-gray-300">|</span>
                    <Link to="/dashboard/skilled-nursing/wound-care" className="text-[#ea580c] hover:underline text-sm">Wound Care</Link>
                    <span className="text-gray-300">|</span>
                    <Link to="/dashboard/skilled-nursing/iv-therapy" className="text-[#ea580c] hover:underline text-sm">IV Therapy</Link>
                    <span className="text-gray-300">|</span>
                    <Link to="/dashboard/skilled-nursing/pca" className="text-[#ea580c] hover:underline text-sm">PCA</Link>
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  {recentSkilledNursingForms.map((form) => (
                    <Link key={form.id} to={form.link} className="block p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
                      <div className="flex items-start justify-between">
                        <div className="flex items-center space-x-3">
                          <span className="text-2xl">{form.formTypeIcon}</span>
                          <div>
                            <p className="font-semibold text-gray-800">{form.patientName || 'Unknown Patient'}</p>
                            <p className="text-sm text-gray-600">{form.formTypeLabel}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <span className={`px-2 py-1 rounded text-xs font-medium ${
                            form.status === 'Completed' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                          }`}>
                            {form.status || 'Draft'}
                          </span>
                          <p className="text-xs text-gray-500 mt-1">
                            {new Date(form.createdAt).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Upcoming Appointments */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
          <div className="p-6 border-b border-gray-200 flex items-center justify-between">
            <h2 className="text-xl font-bold text-gray-800">Today's Appointments</h2>
            <Link to="/dashboard/scheduling" className="text-sm text-[#ea580c] hover:underline">View all</Link>
          </div>
          <div className="p-6">
            {appointments.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Calendar className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                <p>No appointments scheduled</p>
                <Link to="/dashboard/scheduling" className="text-[#ea580c] hover:underline text-sm">Schedule a visit</Link>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {appointments.map((appointment) => (
                  <div key={appointment.id} className="p-4 border border-gray-200 rounded-lg">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <p className="font-semibold text-gray-800">{appointment.patientName}</p>
                        <p className="text-sm text-gray-600">{appointment.type}</p>
                      </div>
                      <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs font-medium">
                        {appointment.time}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600">Clinician: {appointment.clinician}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Alerts */}
        {alertCount > 0 && (
          <Link to="/dashboard/alerts" className="block">
            <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-6 hover:bg-yellow-100 transition-colors">
              <div className="flex items-start space-x-3">
                <AlertCircle className="w-6 h-6 text-yellow-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-semibold text-yellow-900 mb-1">Action Required</h3>
                  <p className="text-yellow-800">
                    You have {alertCount} urgent alert{alertCount !== 1 ? 's' : ''} that need{alertCount === 1 ? 's' : ''} attention.
                  </p>
                </div>
              </div>
            </div>
          </Link>
        )}
      </div>
    </DashboardLayout>
  );
};

export default DashboardHome;
